/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { authStore } from "../../../lib/api";
import { useApp } from "../../../context/AppContext";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { Send, Paperclip, X, Loader2, FileText, Film, File as FileIcon, Layers, Play, Reply, Pencil, Undo2, Check, CornerDownRight, ListTodo, UploadCloud, Archive, Sparkles, AlertTriangle, PlusCircle } from "lucide-react";
import { MentionInput, MentionText } from "./MentionInput";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import { fmtT, liveComments, DESIGN_UPLOAD_ACCEPT } from "./meta";
import { T, getLang } from "../../../lib/i18n";
import { TaskAckCard } from "./TaskAck";
import { CommentFilesLibrary } from "./CommentFilesLibrary";
import { showContractWarnings } from "../../../lib/contractWarn";
import { uploadChunked, uploadErrText, UPLOAD_MAX } from "../../../lib/upload";
/* 【2026-10-29 时区统一】评论待办的默认完成日与日期控件 min 一律按美国日历日：
   原来用 UTC 的 toISOString() 截日期，洛杉矶 16:00 之后会给出次日，
   与页面上显示的美国日期差一天。 */
import { usToday } from "../../../lib/usDate";
// 附件行的「上传于」取文件真实上传日（主线 2026-10-29 新增），与上面时区口径无关。
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";
/* 【2026-10-30 输入侧时区提示】评论里派的待办「完成日期」按美国日历日落库
   （默认值与 min 都取 usToday，见上一行），中国同事按北京日期填会派到前一天。 */
import { UsDateHint } from "./UsDateHint";

const BASE = process.env.REACT_APP_BACKEND_URL;
const tok = () => authStore.get("ali_token") || "";
const fileUrl = (fid) => `${BASE}/api/files/${fid}/download?auth=${tok()}`;
const thumbUrl = (fid) => `${BASE}/api/files/${fid}/thumb?auth=${tok()}`;
/* 美国日历日 + N 天：按 UTC 正午做纯日历加减，避开夏令时与本地时区对日界的影响。 */
const datePlusDays = (ymd, days) => {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  t.setUTCDate(t.getUTCDate() + days);
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
};
// 评论附件同样放开到设计部共享白名单（2026-10-20）：PPT / 旧版 Office / CDR 等
// 设计沟通附件可以直接在评论区传，不必先转换格式。
const ACCEPT = DESIGN_UPLOAD_ACCEPT;
const ACCEPT_EXT = new Set(ACCEPT.split(",").map((x) => x.slice(1)));
const CAT_SHORT = { pdf: "PDF", cad: "CAD", photo: "照片", video: "视频", doc: "文档", other: "其他" };
const CAT_CLS = { pdf: "bg-red-600", cad: "bg-sky-600", photo: "bg-emerald-600", video: "bg-indigo-600", doc: "bg-amber-600", other: "bg-slate-500" };
/* 阶段会话的负责人芯片来源标签：与后端 effectiveManagers.source 对齐。
   2026-10-19 用户要求「不要 @ 下拉，直接把项目负责人放在输入框上方」，
   这些标签用来区分 US / CN / 交图员 / 协作负责人，避免同名时选错人。 */
const OWNER_SOURCE_LABEL = { owner: "US", cdDesigner: "CN", submissionOwner: "交图", collaborator: "协作", independent: "负责人", manager: "项目" };
const commentCandidates = (p) => {
  const configured = (p?.effectiveManagers || []).filter((u) => u?.username && u.resolved !== false);
  const byUsername = new Map(configured.map((u) => [u.username, {
    username: u.username,
    name: u.name || u.username,
    nameZh: u.nameZh || "",
    role: u.role || u.source || "",
    isDesign: true,
  }]));
  return [...byUsername.values()];
};
const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes("Files");
const MAX = UPLOAD_MAX;
/* 2026-10-19 用户需求：编辑仍保留 10 分钟窗口；撤回单独收紧到 5 分钟。
   两个动作必须分开计时，否则改撤回窗口会把编辑也一起缩短。 */
const EDIT_WINDOW_MS = 10 * 60 * 1000;
const RECALL_WINDOW_MS = 5 * 60 * 1000;
/* 【微信式聊天窗口 · 2026-10-22 用户需求】
   ① 消息从上到下按时间升序，最新的在最下面；
   ② 进入只渲染最近 CHAT_PAGE 条，向上滚到顶部自动补 CHAT_MORE 条历史；
   ③ 自己发的靠右、别人发的靠左（气泡 + 头像），一眼分出谁在说话。
   这三条只作用于 composerBottom 的「阶段会话」抽屉（见 chatMode），
   评论页 / 侧栏评论仍保持「最新在上」的评论列表形态，两者语义不同。
   后端项目详情按 at 倒序返回最近 150 条（design_v3.py），这里翻序后做前端分页；
   150 条以内不需要新接口，超过再谈服务端分页。 */
const CHAT_PAGE = 30;
const CHAT_MORE = 30;
const TOP_TRIGGER = 80;
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const extOf = (n = "") => (n.split(".").pop() || "").toUpperCase();
const within = (c, ms = EDIT_WINDOW_MS) => Date.now() - new Date(c.at).getTime() < ms;
const keysOf = (f) => (f ? String(f).split(",").filter(Boolean) : []);

/* 评论：可选所评论的阶段 · 附件（图片 / PDF / 视频 / 图纸，≤200MB）· 缩略图点击放大 · 按阶段筛选（filter 可受控，支持 "a,b" 多键）· 回复（单层）· 作者 10 分钟内可改、5 分钟内可撤回。
   2026-10-19 用户反馈「别人发的消息为什么也显示撤回」：撤回只针对作者自己的发言，
   不再给经理开放撤回他人评论的入口，避免看起来像管理员可以替别人撤回。 */
export const Comments = ({ p, meta, api, refresh, compact, tall, fixedStage, hideLibrary, filter: ctlFilter, onFilter, composerBottom = false, ownerOptions = [], locked = false, lockReason = "" }) => {
  const [innerFilter, setInnerFilter] = useState("");
  const filter = ctlFilter !== undefined ? ctlFilter : innerFilter;
  const setFilter = onFilter || setInnerFilter;
  const [lb, setLb] = useState(null);
  const [drag, setDrag] = useState(0);
  const [incoming, setIncoming] = useState(null);
  const sfx = compact ? "-side" : "";
  // 阶段顺序锁定时不允许拖拽上传：拖进来的附件最终也是发一条评论，
  // 与禁用输入框保持同一口径，避免用户以为拖上去就能发。
  const onDragEnter = (e) => { if (locked || !hasFiles(e)) return; e.preventDefault(); setDrag((n) => n + 1); };
  const onDragLeave = (e) => { if (locked || !hasFiles(e)) return; e.preventDefault(); setDrag((n) => Math.max(0, n - 1)); };
  const onDragOver = (e) => { if (locked || !hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
  const onDrop = (e) => { if (locked || !hasFiles(e)) return; e.preventDefault(); setDrag(0); const fs = [...(e.dataTransfer.files || [])]; if (fs.length) setIncoming({ files: fs, at: Date.now() }); };
  const stages = meta?.stageTemplate || [];
  /* 阶段聊天抽屉只显示并写入当前阶段：阶段上下文由调用方固定，避免用户在同一个窗口里
     把消息写到别的阶段，导致聊天内容和右侧阶段产物、详情阶段视图对不上。

     【2026-10-22 P+B 合并】fixedStage 允许传多个阶段（逗号分隔）：
     P+B 同步提交模式下，Planning Review 与 Building Review 在界面上是**一个节点**，
     历史评论却分别写在两个阶段上（改造前两个阶段各有各的会话）。合并后若只认一个 key，
     另一半的讨论会凭空消失。所以这里：读 = 组内全部阶段一起显示，写 = 组内第一个
     （主阶段）。写入侧必须收敛到单一 key —— 评论接口按 stageKey 校验，传 "a,b" 会 400。 */
  const fixedKeys = fixedStage ? String(fixedStage).split(",").filter(Boolean) : [];
  const fixedKey = fixedKeys[0] || "";
  const active = fixedKeys.length ? fixedKeys : keysOf(filter);
  const live = liveComments(p);
  const roots = p.comments.filter((c) => !c.parentId || !p.comments.some((x) => x.id === c.parentId));
  const repliesOf = (id) => p.comments.filter((c) => c.parentId === id).sort((a, b) => a.at.localeCompare(b.at));
  /* 聊天窗口（composerBottom）按时间升序；评论列表保持后端返回的倒序（最新在上）。 */
  const chatMode = !!composerBottom;
  const ordered = chatMode ? [...roots].sort((a, b) => String(a.at).localeCompare(String(b.at))) : roots;
  /* 【聊天窗口按时间平铺全部消息 · 2026-10-23 用户反馈】
     用户原话：「所有的消息应该最下面是最新的啊...现在如果我现在回复之前的消息...
     消息会插到中间去...那么你之前做的点击以后定位对应消息那个框就不对..直接滚动过去就行」。
     根因：原实现沿用评论页的「回复嵌在根评论下面」（见下面的 repliesOf），于是回复一条
     10/08 的消息，新内容会出现在 10/08 那条**下面**——从最新往回看，新消息插在了历史中间，
     看上去像消息顺序乱了。
     聊天窗口改为**根评论与回复一视同仁、全部按时间平铺**：最新的永远在最底部；
     「在回复哪一条」由气泡上的引用条交代，点引用条直接滚到那条（不再需要绕到根评论）。
     评论页（非 chat）保持卡片 + 缩进回复不变，那是评论列表语义。 */
  const flat = chatMode ? [...(p.comments || [])].sort((a, b) => String(a.at).localeCompare(String(b.at))) : [];
  const scoped = active.length ? (chatMode ? flat : ordered).filter((c) => active.includes(c.stageKey)) : (chatMode ? flat : ordered);
  const [shown, setShown] = useState(CHAT_PAGE);
  const list = chatMode ? scoped.slice(Math.max(0, scoped.length - shown)) : scoped;
  const olderCount = chatMode ? Math.max(0, scoped.length - list.length) : 0;
  const listKey = `${list.length}:${list[0]?.id || ""}:${list[list.length - 1]?.id || ""}`;
  const box = useRef(null);
  const wrap = useRef(null);        // 整个评论区（聊天 / 评论页都在里面），用于「跳到被引用的消息」
  const [flash, setFlash] = useState("");   // 点击引用条后短暂高亮的评论 id
  const [jump, setJump] = useState(null);   // {id}：待滚动的目标评论
  const jumpTry = useRef(0);
  const stick = useRef(true);       // 视口是否贴着底部（在底部才自动跟随新消息）
  const olderBusy = useRef(false);
  const inited = useRef(false);
  // 换项目 / 换阶段 → 回到「首屏 + 贴底」状态
  useEffect(() => { inited.current = false; stick.current = true; setShown(CHAT_PAGE); }, [p.id, fixedStage]);
  useLayoutEffect(() => {
    if (!chatMode) return undefined;
    const el = box.current;
    if (!el || !list.length) return undefined;
    if (!inited.current) { el.scrollTop = el.scrollHeight; inited.current = true; return undefined; } // 进入即停在最新一条
    if (stick.current) el.scrollTop = el.scrollHeight;                                                  // 贴底时才跟随
    return undefined;
  }, [chatMode, listKey]);
  /* 上翻加载：只在滚到接近顶部时补一页；旧消息插在上方，用「新的总高 - 旧的总高」回补 scrollTop，视口不跳。 */
  const loadOlder = () => {
    const el = box.current;
    if (!el || olderBusy.current || olderCount === 0) return;
    olderBusy.current = true;
    const h = el.scrollHeight, top = el.scrollTop;
    setShown((v) => v + CHAT_MORE);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const n = box.current;
      if (n) n.scrollTop = n.scrollHeight - h + top;
      olderBusy.current = false;
    }));
  };
  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < TOP_TRIGGER) loadOlder();
  };
  /* 首屏 30 条撑不满一屏时没有滚动条，就永远滚不到顶部 → 自动再补一页，直到可滚动或没有更多 */
  useEffect(() => {
    if (!chatMode) return;
    const el = box.current;
    if (!el || !olderCount) return;
    if (el.scrollHeight <= el.clientHeight + 8) loadOlder();
  }, [chatMode, listKey, olderCount]); // eslint-disable-line
  const countOf = (k) => live.filter((c) => c.stageKey === k).length;
  const usedStages = stages.filter((s) => countOf(s.key) > 0);
  /* 【引用定位 · 2026-10-23 用户需求】
     「回复别人以后自己看不到是回复的哪一条」——所以回复气泡里要显示被回复消息的开头几个字，
     并且点一下能滚到那条消息。
     目标可能在 chat 分页之外（更早的历史），也可能被当前阶段筛选挡住，两种情况分别处理：
     · 分页之外 → 先补一页再滚（见 jumpTo）；
     · 被筛选挡住 → 提示用户（滚动也找不到，不能假装成功）。 */
  const byId = new Map((p.comments || []).map((x) => [x.id, x]));
  const quoteOf = (c) => byId.get(c.replyToCommentId) || byId.get(c.parentId) || null;
  const jumpTo = (id) => {
    if (!id || !byId.has(id)) { toast.message(T("原消息已不在当前列表中")); return; }
    // 平铺后每条消息都在列表里，不用再折算到根评论；直接滚到被引用那条。
    if (chatMode) {
      const idx = scoped.findIndex((c) => c.id === id);
      if (idx < 0) { toast.message(T("这条消息不在当前阶段 / 筛选范围内")); return; }
      const visibleFrom = scoped.length - shown;
      if (idx < visibleFrom) { stick.current = false; setShown(scoped.length - idx + 5); }
    }
    jumpTry.current = 0;
    setJump({ id, at: Date.now() });
  };
  /* 滚动 + 短暂高亮。目标还没渲染出来（刚补的分页）就下一帧重试，最多 10 次后放弃提示。 */
  useLayoutEffect(() => {
    if (!jump) return undefined;
    const el = wrap.current;
    const target = el?.querySelector(`[data-cid="${jump.id}"]`);
    if (!target) {
      if (jumpTry.current < 10) {
        jumpTry.current += 1;
        const t = setTimeout(() => setJump((j) => (j ? { ...j } : j)), 60);
        return () => clearTimeout(t);
      }
      toast.message(T("找不到这条消息，可能已被撤回或不在当前筛选内"));
      setJump(null);
      return undefined;
    }
    jumpTry.current = 0;
    stick.current = false;                       // 跳转时不要被「贴底跟随」拉回最新一条
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    setFlash(jump.id);
    const t1 = setTimeout(() => setFlash(""), 1600);
    setJump(null);
    return () => clearTimeout(t1);
  }, [jump, listKey]); // eslint-disable-line
  const openLb = (c, i) => setLb({ items: c.files.map((f) => ({ id: f.fileId, url: fileUrl(f.fileId), thumbUrl: f.kind === "pdf" ? thumbUrl(f.fileId) : undefined, kind: f.kind, name: f.name, meta: `${fmtSize(f.size)} · ${c.author} · ${T("评论时间：{0}", [fmtT(c.at)])} · ${uploadDateLabel(f.uploadedAt)}` })), index: i });
  /* 2026-10-19 阶段会话：聊天输入框要固定在抽屉底部，消息列表在上方独立滚动。
     桌面端评论页 / 侧栏评论仍保持原来的顶部输入框，只有传 composerBottom 的聊天窗口切换布局。 */
  const listCls = composerBottom ? "space-y-3 flex-1 min-h-0 overflow-y-auto pr-0.5" : compact ? "space-y-2 max-h-[60vh] overflow-y-auto pr-0.5" : tall ? "space-y-3 max-h-[calc(100dvh-19rem)] overflow-y-auto pr-0.5" : "space-y-3";
  const toggleKey = (k) => setFilter(active.length === 1 && active[0] === k ? "" : k);
  const mentionUsers = useMemo(() => commentCandidates(p), [p]);
  const composer = <Composer p={p} meta={meta} api={api} refresh={refresh} compact={compact} base="dv3-comment" sfx={sfx} withStage={!fixedStage} fixedStage={fixedKey} incoming={incoming} dropUp={composerBottom} ownerOptions={ownerOptions} mentionCandidates={mentionUsers} requireOwner={!!fixedStage} locked={locked} lockReason={lockReason} />;
  return (
    <div ref={wrap} className={`relative rounded-lg ${compact ? "space-y-2" : "space-y-3"} ${composerBottom ? "flex min-h-0 flex-1 flex-col" : ""} ${drag ? "ring-2 ring-violet-400 ring-offset-2" : ""}`} data-testid={compact ? "dv3-comments-side" : "dv3-comments"} data-filter={filter} data-dragging={drag ? 1 : 0}
      onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
      {drag > 0 && (
        <div className="absolute inset-0 z-20 rounded-lg bg-violet-600/85 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 text-white pointer-events-none" data-testid={`dv3-comment-dropzone${sfx}`}>
          <UploadCloud className="h-9 w-9" />
          <p className="text-sm font-bold">{T("松开上传到评论")}</p>
          <p className="text-[11px] opacity-90">{T("自动识别 类型（图纸 PDF / CAD / 照片 / 视频 / 文档）与 阶段 · 匹配到的归档到阶段任务 · 大文件分片续传 ≤200MB")}</p>
        </div>
      )}
      {!composerBottom && composer}
      {!fixedStage && usedStages.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`dv3-comment-filter${sfx}`}>
          <button type="button" onClick={() => setFilter("")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2 py-0.5 rounded-full border font-semibold ${!active.length ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`dv3-comment-filter-all${sfx}`}>{T("全部")} {live.length}</button>
          {usedStages.map((s) => <button key={s.key} type="button" onClick={() => toggleKey(s.key)} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2 py-0.5 rounded-full border font-semibold truncate max-w-[180px] ${active.includes(s.key) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-700 border-violet-200 hover:bg-violet-50"}`} data-testid={`dv3-comment-filter-${s.key}${sfx}`}>{T(s.name)} {countOf(s.key)}</button>)}
        </div>
      )}
      {list.length === 0 && <p className="text-xs text-slate-400 text-center py-6" data-testid={compact ? "dv3-comments-side-empty" : "dv3-comments-empty"}>{active.length ? T("该阶段暂无评论") : T("暂无评论")}</p>}
      <div ref={chatMode ? box : undefined} onScroll={chatMode ? onScroll : undefined} className={listCls} data-testid={`dv3-comment-list${sfx}`} data-count={list.length} data-older={olderCount}>
        {chatMode && olderCount > 0 && <p className="text-center text-[10px] text-slate-400 py-1" data-testid={`dv3-comment-older${sfx}`}>{T("向上滚动加载更早的消息 · 还有 {0} 条", [olderCount])}</p>}
        {chatMode && olderCount === 0 && list.length > 0 && <p className="text-center text-[10px] text-slate-300 py-1" data-testid={`dv3-comment-begin${sfx}`}>{T("— 已到最早消息 —")}</p>}
        {list.map((c) => (chatMode ? (
          /* 聊天形态（2026-10-23 改为按时间平铺）：根评论与回复一视同仁，最新的在最底部。
             不再把回复缩进嵌到根评论下面 —— 那样回复一条旧消息会把新内容插进历史中间。
             「在回复哪一条」由气泡上的引用条交代（可点击定位）。 */
          <div key={c.id} data-cid={c.id} className={`space-y-1.5 rounded-xl transition-colors ${flash === c.id ? "bg-amber-50 ring-2 ring-amber-300" : ""}`} data-testid={`dv3-comment-${c.id}${sfx}`} data-stage={c.stageKey || ""} data-replies={repliesOf(c.id).length} data-recalled={c.deleted ? "1" : "0"} data-chat="1" data-parent={c.parentId || undefined}>
            <CommentItem c={c} p={p} meta={meta} api={api} refresh={refresh} compact={compact} sfx={sfx} fixedStage={fixedKey} ownerOptions={ownerOptions} onOpenLb={openLb} onStage={toggleKey} onJump={jumpTo} quoteOf={quoteOf} canReply chat replyTo={c.parentId ? byId.get(c.parentId) : null} />
          </div>
        ) : (
          <div key={c.id} data-cid={c.id} className={`rounded-lg border transition-colors ${flash === c.id ? "bg-amber-50 ring-2 ring-amber-300 border-amber-300" : c.deleted ? "bg-slate-50/70 border-dashed border-slate-200 px-3 py-2" : `bg-white border-slate-200 ${compact ? "p-2.5" : "p-3"}`}`} data-testid={`dv3-comment-${c.id}${sfx}`} data-stage={c.stageKey || ""} data-replies={repliesOf(c.id).length} data-recalled={c.deleted ? "1" : "0"}>
            <CommentItem c={c} p={p} meta={meta} api={api} refresh={refresh} compact={compact} sfx={sfx} fixedStage={fixedKey} ownerOptions={ownerOptions} onOpenLb={openLb} onStage={toggleKey} onJump={jumpTo} quoteOf={quoteOf} canReply />
            {repliesOf(c.id).map((r) => (
              <div key={r.id} data-cid={r.id} className={`flex-wrap md:flex-nowrap mt-2 ml-3 pl-3 border-l-2 border-violet-100 flex gap-1.5 rounded-lg transition-colors ${flash === r.id ? "bg-amber-50 ring-2 ring-amber-300" : ""}`} data-testid={`dv3-comment-${r.id}${sfx}`} data-parent={c.id}>
                <CornerDownRight className="h-3.5 w-3.5 text-violet-300 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1"><CommentItem c={r} p={p} meta={meta} api={api} refresh={refresh} compact={compact} sfx={sfx} fixedStage={fixedKey} ownerOptions={ownerOptions} onOpenLb={openLb} onJump={jumpTo} quoteOf={quoteOf} replyTo={c} canReply /></div>
              </div>
            ))}
          </div>
        )))}
      </div>
      {composerBottom && <div className="shrink-0 pt-1">{composer}</div>}
      {!hideLibrary && <CommentFilesLibrary p={p} api={api} meta={meta} sfx={sfx} tick={p.comments.length} />}
      {lb && <MediaLightbox tid={`dv3-comment-lightbox${sfx}`} items={lb.items} index={lb.index} onIndex={(i) => setLb((v) => ({ ...v, index: i }))} onClose={() => setLb(null)} />}
    </div>
  );
};

/* 一条评论 / 回复：作者 + 时间 + 阶段胶囊 · 文字 · 附件 · 操作（回复 / 编辑 / 删除）· 内联编辑与回复框
   chat=true 时切换成「微信聊天」形态：自己靠右（品牌紫气泡）、别人靠左（白气泡），
   头像贴在外侧，作者名只在别人消息上出现，阶段胶囊隐藏 —— 窗口本身已经固定了阶段，
   再逐条重复只会占地方。 */
const CommentItem = ({ c, p, meta, api, refresh, compact, sfx, fixedStage, ownerOptions = [], onOpenLb, onStage, onJump, quoteOf, canReply, chat = false, replyTo = null }) => {
  const { user } = useApp();
  const [mode, setMode] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = user?.username === c.authorUsername;
  const canEdit = mine && within(c);
  /* 撤回入口只属于作者本人，且必须在 5 分钟内（2026-10-19 用户反馈）：
     别人发的消息不显示撤回按钮；即使是经理，也只能撤回自己在时限内的评论。
     后端 DELETE 接口同步做作者校验，前端藏按钮只是第一道拦截。 */
  const canDel = mine && within(c, RECALL_WINDOW_MS);
  const tid = (k) => `dv3-comment-${k}-${c.id}${sfx}`;
  const save = async () => {
    const text = draft.trim();
    if (!text && !c.files?.length) return toast.error(T("评论不能为空"));
    setBusy(true);
    try { await api.patch(`/design/v3/projects/${p.id}/comments/${c.id}`, { text }); setMode(null); refresh(); } catch (e) { toast.error(e?.detailText || e.message); }
    setBusy(false);
  };
  const del = async () => {
    if (!window.confirm(T("确定撤回这条评论？"))) return;
    setBusy(true);
    try { await api.delete(`/design/v3/projects/${p.id}/comments/${c.id}`); toast.success(T("已撤回")); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    /* 成功或超过 5 分钟被后端拦截，都重新拉一次聊天数据：
       成功时去掉内容，失败时让按钮/内容状态与后端时限保持一致，避免停留在旧界面。 */
    finally { setBusy(false); refresh(); }
  };
  /* 已撤回：只留一行灰字占位（微信式），正文 / 附件 / 操作一律不渲染，但记录仍在库里 */
  if (c.deleted) {
    const bySelf = !c.deletedBy || c.deletedBy === c.author;
    return (
      <p className={`text-[11px] text-slate-400 flex flex-wrap items-center gap-x-1.5 ${chat ? "justify-center" : ""}`} data-testid={tid("item")} data-recalled="1"
        title={meta?.isManager && c.deletedAt ? T("由 {0} 于 {1} 撤回", [c.deletedBy || "—", fmtT(c.deletedAt)]) : undefined}>
        <span className="italic" data-testid={tid("recalled")}>{bySelf ? T("{0} 撤回了一条评论", [c.author]) : T("{0} 的评论已被 {1} 撤回", [c.author, c.deletedBy])}</span>
        <span className="text-slate-300">{fmtT(c.at)}</span>
      </p>
    );
  }
  const acts = (
    <span className={`${chat ? "" : "ml-auto"} inline-flex items-center gap-1.5 text-slate-400`}>
      {/* 回复自己的消息 = 给自己那条补充一句（2026-10-23 用户口径），按钮文案跟着变，
          避免看起来像在跟自己对话；入口本身保留（不能回自己就等于被迫再 @ 别人一次）。 */}
      {canReply && <button type="button" onClick={() => setMode(mode === "reply" ? null : "reply")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 hover:text-violet-700" data-testid={tid("reply-btn")} data-self-note={mine ? "1" : "0"}>{mine ? <PlusCircle className="h-3.5 w-3.5" /> : <Reply className="h-3.5 w-3.5" />}{mine ? T("补充描述") : T("回复")}</button>}
      {canEdit && <button type="button" onClick={() => { setDraft(c.text || ""); setMode(mode === "edit" ? null : "edit"); }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 hover:text-violet-700" title={T("编辑（发表后 10 分钟内）")} data-testid={tid("edit-btn")}><Pencil className="h-3.5 w-3.5" /></button>}
      {/* 2026-10-19：撤回改成带文字的按钮，避免和前面的图标按钮长得差不多；
          悬停只说明动作本身，不再展开「已撤回 / 库里留档」等解释。 */}
      {canDel && <button type="button" onClick={del} disabled={busy} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 hover:text-amber-600" title={T("撤回")} data-testid={tid("delete-btn")} data-action="recall"><Undo2 className="h-3.5 w-3.5" />{T("撤回")}</button>}
    </span>
  );
  const head = chat ? (
    <p className={`flex flex-wrap items-center gap-x-1.5 text-[10px] text-slate-400 ${mine ? "justify-end" : ""}`}>
      {!mine && <b className="text-slate-600">{c.author}</b>}
      <span title={c.at}>{fmtT(c.at)}</span>
      {c.editedAt && <span className="italic" data-testid={tid("edited")}>{T("（已编辑）")}</span>}
      {acts}
    </p>
  ) : (
    <p className="text-[11px] text-slate-400 flex flex-wrap items-center gap-x-1.5"><b className="text-slate-700">{c.author}</b> · {fmtT(c.at)}{c.editedAt && <span className="italic" data-testid={tid("edited")}>{T("（已编辑）")}</span>}
      {c.stageName && !c.parentId && <button type="button" onClick={() => onStage?.(c.stageKey)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-semibold hover:bg-violet-100" data-testid={tid("stage-chip")} title={T("点击只看该阶段的评论")}><Layers className="h-3 w-3" />{T(c.stageName)}</button>}
      {acts}
    </p>
  );
  /* 回复标注（2026-10-22 用户需求）：
     「回复的那个人就是他想 @ 的负责人，所以不用选」——不用选，但必须标出来。
     这里在消息顶部渲染一条引用条「回复 @某某」，聊天窗口与评论页同款。

     【2026-10-23 修正】支持「回复的回复」后，标注不能再取根评论作者：
     截图里 Arthi 回复 Iris、DouDou 又回复 Arthi，再回 DouDou 时应显示「回复 @DouDou」。
     后端把直接回复对象记在 replyToUsername / replyToName，优先用它；
     老数据（本次改造前写入）没有这两个字段，退回根评论作者，行为与改造前一致。

     【2026-10-23 追加 · 用户需求「自己看不到是回复的哪一条」】
     只写「回复 @某某」不够 —— 同一个人可能被回很多条，看不出指向哪句话。所以引用条改成：
       「回复 @某某：被回复消息的前几个字」
     并且做成按钮，点一下滚到那条消息并短暂高亮（见 Comments 里的 jumpTo / flash）。

     【2026-10-23 追加 · 用户需求「自己回复自己算补充描述」】
     回复自己的消息时语义不是「对话」，而是给自己刚发的那条补充一句，所以：
       · 文案改成「补充描述 @自己」，不再是「回复」；
       · 不产生提醒（后端 _comment_notify 里自己回复自己不通知，见那段注释），
         也不会连带把原本 @ 的负责人再提醒一遍 —— 用户原话：「再次发送就不会同时给
         那个 @ 的负责人发送多条」。 */
  const quote = quoteOf?.(c) || null;
  const replyAuthor = c.replyToName || replyTo?.author || quote?.author || "";
  const replyAuthorName = c.replyToUsername || replyTo?.authorUsername || quote?.authorUsername || "";
  const selfNote = !!c.parentId && replyAuthorName === c.authorUsername;   // 回复自己的消息 = 补充描述
  const rawQuote = (quote?.text || "").replace(/\s+/g, " ").trim();
  const quotePreview = rawQuote.length > 40 ? `${rawQuote.slice(0, 40)}…` : rawQuote;
  /* 只有附件时也要能认出是哪一条：用附件文件名（PDF 缩略图 / 图片本来就靠文件名区分），
     拿不到文件名才退回数量描述。 */
  const firstFileName = quote?.files?.[0]?.name || "";
  const quoteFiles = !rawQuote && quote?.files?.length
    ? (firstFileName ? `${firstFileName}${quote.files.length > 1 ? T(" 等 {0} 个附件", [quote.files.length]) : ""}` : T("[{0} 个附件]", [quote.files.length]))
    : "";
  /* 补充描述是「给自己那条加句话」，@ 自己名字对读者没有信息量，而且会像在自我提醒；
     这里只写「补充描述：原文摘要」，谁补充的由气泡本身的作者名交代。 */
  const replyLabel = c.parentId && replyAuthor
    ? selfNote
      ? `${T("补充描述")}${quotePreview || quoteFiles ? `：${quotePreview || quoteFiles}` : ""}`
      : `${T("回复 @{0}", [replyAuthor])}${quotePreview || quoteFiles ? `：${quotePreview || quoteFiles}` : ""}`
    : "";
  const body = mode === "edit" ? (
    <div className={`flex-wrap md:flex-nowrap mt-1 flex gap-1.5 items-start ${chat ? "w-full max-w-[420px]" : ""}`} data-testid={tid("edit-box")}>
      <MentionInput tid={tid("edit-input")} value={draft} onChange={setDraft} onSubmit={save} className="flex-1 text-sm border border-violet-300 rounded-md px-3 py-1.5 min-w-0" />
      <Button size="sm" onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-700 h-8" data-testid={tid("edit-save")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</Button>
      <Button size="sm" variant="outline" onClick={() => setMode(null)} className="h-8" data-testid={tid("edit-cancel")}><X className="h-4 w-4" /></Button>
    </div>
  ) : (
    <>
      {replyLabel && (
        /* 引用条：点一下跳到被引用的那条消息（jumpTo → 滚动 + 高亮）。
           用 button 而不是 p，让「可点击」在键盘 / 触屏上也成立；没有 onJump 时退化成纯文字。 */
        <button type="button" onClick={() => onJump?.(quote?.id || c.replyToCommentId || c.parentId)} disabled={!onJump}
          className="mb-1 flex w-full max-w-full items-start gap-1 rounded-lg border-l-2 border-violet-300 bg-slate-50 px-1.5 py-1 text-left text-[10px] font-semibold text-slate-500 enabled:hover:bg-slate-100"
          data-testid={tid("reply-to")} data-reply-to={replyAuthorName} data-quote-id={quote?.id || ""} data-self-note={selfNote ? "1" : "0"}
          title={T("点击跳到被引用的消息")}>
          <CornerDownRight className="mt-px h-3 w-3 shrink-0 text-violet-400" />
          <span className="min-w-0 flex-1 truncate">{replyLabel}</span>
        </button>
      )}
      {c.text && <p className={`mt-0.5 whitespace-pre-wrap text-slate-800 ${compact ? "text-xs" : "text-sm"}`} data-testid={tid("text")}><MentionText text={c.text} mentions={c.mentions} /></p>}
      {c.files?.length > 0 && <Attachments c={c} sfx={sfx} onOpen={(i) => onOpenLb(c, i)} />}
    </>
  );
  const tail = (
    <>
      {/* 阶段会话没有 @ 下拉，提醒对象来自输入框上方的负责人芯片。
          这里把当时选中的负责人显式写出来，方便回看「这条消息提醒了谁」。 */}
      {/* 回复消息不重复显示「负责人：@」——上面的「回复 @某某」已经说明了提醒对象 */}
      {!c.parentId && (c.ownerMentions || []).length > 0 && (
        <p className="mt-0.5 text-[10px] font-semibold text-violet-700" data-testid={tid("owners")}>
          {T("负责人：@ {0}", [(c.mentions?.length ? c.mentions : c.ownerMentions).join("、")])}
        </p>
      )}
      {(() => { const t = (p.tasks || []).find((x) => x.commentId === c.id || x.id === c.taskId); return t ? <div className={`mt-1.5 ${chat ? "w-full" : ""}`}><TaskAckCard t={t} api={api} meta={meta} onChanged={refresh} compact={compact} tid={`dv3-comment-task-${c.id}${sfx}`} /></div> : null; })()}
      {/* 【回复不强制选负责人 · 2026-10-22 用户反馈】
          「点回复不用必选提醒人 —— 回复的那个人就是他想 @ 的负责人」。
          所以回复框不传 requireOwner：不显示负责人芯片、不校验 owners；
          但要把被回复人当成提醒对象带上去（replyTo），发送时自动变成 ownerMentions，
          后端照常给他发「负责人提醒」，消息上也会标「回复 @某某」。
          主写稿框（阶段会话底部）仍然必选负责人，那是「发起一条新消息」的规则。

          【回复自己 · 2026-10-23 用户口径】回自己的消息 = 补充描述：文案换掉，且**不**自动
          带提醒（回复框拿到 replyTo 但 selfReply 为真时 Composer 不发 ownerMentions，
          后端也不会给自己发提醒），避免自己收自己的提醒、也不连带重复打扰原来 @ 的人。 */}
      {mode === "reply" && <div className={`mt-1.5 ${chat ? "w-full min-w-[240px]" : ""}`}><Composer p={p} meta={meta} api={api} refresh={() => { setMode(null); refresh(); }} compact parentId={c.id} fixedStage={fixedStage} base={`dv3-reply-${c.id}`} sfx={sfx} placeholder={mine ? T("补充说明这条消息…（不发送提醒）") : T("回复 {0}…（自动提醒他）", [c.author])} autoFocus onCancel={() => setMode(null)} ownerOptions={ownerOptions} replyTo={c} selfReply={mine} selfNote={mine} /></div>}
    </>
  );
  if (chat) {
    const initial = (c.author || "?").trim().charAt(0).toUpperCase();
    return (
      <div data-testid={tid("item")} data-mine={mine ? "1" : "0"} data-edited={c.editedAt ? "1" : "0"} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
        <span aria-hidden data-testid={tid("avatar")} className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ${mine ? "bg-violet-600 text-white" : "bg-slate-200 text-slate-600"}`}>{initial}</span>
        <div className={`flex min-w-0 max-w-[86%] flex-col ${mine ? "items-end" : "items-start"}`}>
          {head}
          {mode === "edit" ? body : <div className={`mt-0.5 max-w-full rounded-2xl px-3 py-2 text-left ${mine ? "bubble-mine rounded-tr-sm" : "bg-white border border-slate-200/80 rounded-tl-sm"}`} data-testid={tid("bubble")}>{body}</div>}
          {tail}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={tid("item")} data-mine={mine ? "1" : "0"} data-edited={c.editedAt ? "1" : "0"}>
      {head}
      {body}
      {tail}
    </div>
  );
};

/* 撰写框：阶段下拉（withStage）· @ 文本 · 附件（先逐个上传取 fileId 再提交）· 发送；parentId 时为回复。
   selfReply（2026-10-23）：回复自己的消息 = 补充描述，不带任何自动提醒（见 send 里的说明）。 */
const Composer = ({ p, meta, api, refresh, compact, base, sfx, withStage, fixedStage, parentId, placeholder, autoFocus, onCancel, incoming, dropUp = false, ownerOptions = [], mentionCandidates = [], requireOwner = false, locked = false, lockReason = "", replyTo = null, selfReply = false }) => {
  const [t, setT] = useState("");
  const [stage, setStage] = useState(fixedStage || "");
  const [autoStage, setAutoStage] = useState(false);
  const [pend, setPend] = useState([]);
  const [up, setUp] = useState({}); // 发送时逐附件上传状态 {i: {pct, status: queued|uploading|done|cancelled}}
  const [busy, setBusy] = useState(false);
  const ctrlRef = useRef({});
  const cancelRef = useRef(new Set());
  const setUpAt = (i, patch) => setUp((u) => ({ ...u, [i]: { ...(u[i] || {}), ...patch } }));
  const cancelUp = (i) => { cancelRef.current.add(i); ctrlRef.current[i]?.abort(); setUpAt(i, { status: "cancelled" }); };
  const cancelAll = () => Object.entries(up).forEach(([i, s]) => (s.status === "queued" || s.status === "uploading") && cancelUp(Number(i)));
  const [todo, setTodo] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState(() => datePlusDays(usToday(), 7));
  /* 阶段会话的提醒负责人（2026-10-19 用户需求）：
     取消 @ 下拉，直接把项目有效负责人做成输入框上方的芯片；每次发送至少选 1 人，
     发送成功后清空，下一条消息必须重新确认提醒对象。 */
  const [owners, setOwners] = useState([]);
  const ownerKey = (ownerOptions || []).map((u) => u.username).join(",");
  useEffect(() => {
    // 负责人被主管改动后，剔除已经不在项目负责人列表里的旧选择，避免提交无效 username。
    const allow = new Set(ownerKey ? ownerKey.split(",") : []);
    setOwners((v) => {
      const next = v.filter((un) => allow.has(un));
      return next.length === v.length ? v : next;
    });
  }, [ownerKey]);
  const [cands, setCands] = useState(null);
  const inp = useRef(null);
  const tid = (k) => `${base}-${k}${sfx}`;
  const stages = meta?.stageTemplate || [];
  const groups = stages.reduce((m, s) => { (m[s.group] = m[s.group] || []).push(s); return m; }, {});
  const openTodo = async () => {
    setTodo((v) => !v);
    if (!cands) { try { const { data } = await api.get("/design/v3/mention-candidates"); setCands(data || []); } catch { setCands([]); } }
  };
  const mentionedUser = (cands || []).find((u) => [u.name, u.nameZh, u.username].some((nm) => nm && t.includes(`@${nm}`)));
  const effAssignee = assignee || mentionedUser?.username || "";
  /* 加入待发送附件（点选或拖拽）→ 后端即时识别 类型 / 阶段 / 归档槽位；阶段未选时按首个文件自动选中 */
  const classify = async (entries, stageKey) => {
    try {
      const { data } = await api.post(`/design/v3/projects/${p.id}/comments/classify`, { files: entries.map((x) => ({ name: x.file.name, contentType: x.file.type, size: x.file.size })), stageKey: stageKey || null });
      return entries.map((x, i) => ({ ...x, cls: data.items?.[i] || null, canArchive: data.canArchive }));
    } catch { return entries; }
  };
  const addFiles = async (fs) => {
    const bad = fs.find((f) => f.size > MAX);
    if (bad) return toast.error(T("「{0}」超过 200MB", [bad.name]));
    const badExt = fs.find((f) => !ACCEPT_EXT.has((f.name.split(".").pop() || "").toLowerCase()));
    if (badExt) return toast.error(T("「{0}」类型不支持（支持 图片 / PDF / 视频 / CAD / Office（含 PPT）/ CDR / ZIP）", [badExt.name]));
    let entries = fs.map((f) => { const kind = mediaKind(f.name, f.type); return { file: f, kind, url: kind === "photo" ? URL.createObjectURL(f) : null }; });
    entries = await classify(entries, (withStage ? stage : fixedStage) || null);
    let nextStage = stage;
    if (withStage && !stage) {
      const sk = entries.find((x) => x.cls?.stageKey)?.cls?.stageKey;
      if (sk) { nextStage = sk; setStage(sk); setAutoStage(true); }
    }
    if (nextStage !== stage) entries = await classify(entries, nextStage);
    setPend((v) => [...v, ...entries].slice(0, 12));
  };
  const pick = (e) => { const fs = [...(e.target.files || [])]; e.target.value = ""; if (fs.length) addFiles(fs); };
  useEffect(() => { if (incoming?.files?.length) addFiles(incoming.files); }, [incoming]); // eslint-disable-line
  /* Ctrl+V 粘贴截图 / 图片 → 作为附件（自动命名 screenshot-时间.png），同样走自动分类 */
  const onPaste = (e) => {
    const imgs = [...(e.clipboardData?.files || [])].filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    e.preventDefault();
    const ts = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "").replace(/(\d{8})(\d{6})/, "$1-$2");
    addFiles(imgs.map((f, i) => (f.name && f.name !== "image.png" ? f : new File([f], `screenshot-${ts}${imgs.length > 1 ? `-${i + 1}` : ""}.${(f.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: f.type }))));
    toast.message(T("已粘贴 {0} 张截图为附件", [imgs.length]));
  };
  const changeStage = async (sk) => {
    setStage(sk); setAutoStage(false);
    if (pend.length) setPend(await classify(pend, sk));
  };
  const rm = (i) => setPend((v) => { v[i]?.url && URL.revokeObjectURL(v[i].url); return v.filter((_, j) => j !== i); });
  const send = async () => {
    const text = t.trim();
    if (locked) return toast.error(lockReason || T("上一阶段尚未完成，本阶段会话暂不能发送消息"));
    if (!text && !pend.length) return;
    if (requireOwner && !owners.length) return toast.error(T("请至少选择 1 个项目负责人"));
    if (todo && !effAssignee) return toast.error(T("请选择待办负责人（或在评论中 @ 同事）"));
    setBusy(true);
    cancelRef.current = new Set(); ctrlRef.current = {};
    setUp(Object.fromEntries(pend.map((_, i) => [i, { pct: 0, status: "queued" }])));
    try {
      const files = []; let skipped = 0;
      for (let i = 0; i < pend.length; i++) {
        if (cancelRef.current.has(i)) { skipped++; continue; }
        const ctrl = new AbortController(); ctrlRef.current[i] = ctrl;
        setUpAt(i, { status: "uploading" });
        try {
          const src = pend[i].file;
          const up = await uploadChunked(api, src, {
            signal: ctrl.signal,
            onProgress: ({ pct }) => setUpAt(i, { pct }),
          });
          const name = up.filename || src.name;
          const contentType = up.contentType || src.type || "application/octet-stream";
          files.push({
            fileId: up.fileId, name, size: up.size ?? src.size, contentType,
            kind: mediaKind(name, contentType),
          });
          setUpAt(i, { pct: 100, status: "done" });
        } catch (e) {
          if (ctrl.signal.aborted || cancelRef.current.has(i)) { skipped++; setUpAt(i, { status: "cancelled" }); continue; }
          throw e;
        }
      }
      if (skipped) toast.message(T("已取消 {0} 个附件上传", [skipped]));
      if (!text && !files.length) { toast.message(T("附件已全部取消，未发送")); setUp({}); setBusy(false); return; }
      /* 提醒对象：
         · 主写稿框（requireOwner）→ 用芯片选中的 owners；
         · 回复框 → 被回复人就是发送方想 @ 的那个人（2026-10-22 用户口径），自动带上他；
           但他必须在项目负责人列表里才走 ownerMentions（后端会校验，非负责人传了会 400）。
           非负责人的回复不传 ownerMentions —— 后端对 parent 作者本来就会发「回复了你」通知，
           不会漏提醒。
         · 回复自己的消息（selfReply，2026-10-23 用户口径）→ 这是「补充描述」，**不带任何自动提醒**：
           自己不需要收自己的提醒；也不再顺便把原来 @ 的负责人提醒一遍，否则同一个人会因为
           一条补充说明收到多条通知（用户原话：「再次发送就不会同时给那个 @ 的负责人发送多条」）。
         · 其它情况（普通评论区）→ 走正文 @ 解析（正文里手打 @ 仍会提醒，那是用户显式写的）。 */
      const replyOwner = (!requireOwner && !selfReply && parentId && replyTo?.authorUsername
        && (ownerOptions || []).some((u) => u.username === replyTo.authorUsername)) ? [replyTo.authorUsername] : [];
      const mentionPayload = requireOwner ? { ownerMentions: owners } : (replyOwner.length ? { ownerMentions: replyOwner } : {});
      const { data } = await api.post(`/design/v3/projects/${p.id}/comments`, { text, files, stageKey: (stage || fixedStage) || null, parentId: parentId || null, ...mentionPayload, ...(todo ? { task: { assignee: effAssignee, dueAt: due } } : {}) });
      if (data?.task) toast.success(T("已创建待办 → {0} · 截止 {1} · 等待对方接受", [data.task.assigneeName, due]));
      if (data?.archived?.length) {
        const n = data.archived.filter((x) => x.archivedTo).length;
        toast.success(n ? T("附件已自动分类保存：{0} 个归档到阶段任务文件槽，{1} 个进入评论附件库", [n, data.archived.length - n]) : T("附件已自动分类进入评论附件库（{0} 个）", [data.archived.length]));
        // 归档到「合同签署」槽位且门牌与项目地址不符 → 黄色提示（只警告不拦截）
        showContractWarnings(data.archived.flatMap((x) => x.warnings || []));
      }
      setT(""); pend.forEach((x) => x.url && URL.revokeObjectURL(x.url)); setPend([]); setTodo(false); setAssignee(""); setAutoStage(false); setOwners([]); refresh();
    } catch (e) { toast.error(uploadErrText(e, getLang() === "zh")); }
    setUp({}); setBusy(false);
  };
  const upList = Object.values(up);
  const upDone = upList.filter((s) => s.status === "done").length;
  const upLive = upList.some((s) => s.status === "queued" || s.status === "uploading");
  let qn = 0;
  const upLabel = (s) => s.status === "queued" ? T("排队 #{0}", [++qn]) : s.status === "uploading" ? `${s.pct}%` : s.status === "done" ? T("完成") : T("已取消");
  /* 【输入区布局 · 2026-10-22 用户需求】
     ① 待发送附件 / 待办面板 / 上传进度全部放到输入框**上方**：composer 贴在窗口底部，
        任何放在输入行下面的内容都会把输入框整体顶高（附件一多，输入框就往上跳）；
     ② 发送按钮不可点时，在它右侧直接写清原因（而不是只把按钮变灰让人猜）。 */
  const blockReason = busy ? T("发送中…")
    : locked ? (lockReason || T("上一阶段尚未完成"))
      : (requireOwner && !owners.length) ? T("请先选择项目负责人")
        : (!t.trim() && !pend.length) ? T("请输入内容或添加附件")
          : "";
  const pendingTray = (
    <>
      {pend.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={tid("pending")} data-count={pend.length}>
          {pend.map((x, i) => (
            <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={tid(`pending-${i}`)} data-category={x.cls?.category || ""} data-stage={x.cls?.stageKey || ""} data-slot={x.cls?.slot && x.canArchive ? 1 : 0} data-warn={x.cls?.warn ? 1 : 0}
              title={`${x.file.name} · ${fmtSize(x.file.size)}${x.cls ? ` · ${T(x.cls.categoryLabel)}${x.cls.stageName ? ` · ${T(x.cls.stageName)}` : ""}${x.cls.slot && x.canArchive ? ` · ${T("将归档到")} ${x.cls.slot.taskTitle} · ${x.cls.slot.fileType} v${x.cls.slot.version}` : ` · ${T("进入评论附件库")}`}` : ""}${x.cls?.warn ? ` · ⚠ ${getLang() === "zh" ? x.cls.warn.message : (x.cls.warn.messageEn || x.cls.warn.message)}` : ""}`}>
              {x.url ? <img src={x.url} alt="" className="h-full w-full object-cover" /> : <Placeholder kind={x.kind} name={x.file.name} />}
              {/* 门牌软校验命中（合同槽位）：归档前就提示可能挂错，只警告不拦截 */}
              {x.cls?.warn && <span className="absolute top-0.5 left-0.5 rounded-full bg-amber-500 text-white p-0.5 shadow" title={getLang() === "zh" ? x.cls.warn.message : (x.cls.warn.messageEn || x.cls.warn.message)} data-testid={tid(`pending-warn-${i}`)}><AlertTriangle className="h-2.5 w-2.5" /></span>}
              {x.cls && !up[i] && <span className={`absolute bottom-0 inset-x-0 text-white text-[8px] font-bold px-1 leading-4 flex items-center gap-0.5 ${CAT_CLS[x.cls.category] || CAT_CLS.other}`} data-testid={tid(`pending-cls-${i}`)}>{x.cls.slot && x.canArchive && <Archive className="h-2.5 w-2.5" />}<span className="truncate">{T(CAT_SHORT[x.cls.category] || "其他")}{x.cls.slot && x.canArchive ? ` → ${x.cls.slot.fileType}` : ""}</span></span>}
              {up[i] && (
                <span className={`absolute inset-0 flex flex-col items-center justify-end text-white text-[9px] font-bold ${up[i].status === "cancelled" ? "bg-slate-700/70" : "bg-black/55"}`} data-testid={tid(`pending-up-${i}`)} data-status={up[i].status} data-pct={up[i].pct}>
                  <span className="mb-1">{upLabel(up[i])}</span>
                  <span className="h-1 w-full bg-white/30"><span className={`block h-full transition-[width] duration-200 ${up[i].status === "done" ? "bg-emerald-400" : up[i].status === "cancelled" ? "bg-slate-400" : "bg-violet-400"}`} style={{ width: `${up[i].status === "done" ? 100 : up[i].pct}%` }} /></span>
                </span>
              )}
              {busy
                ? (up[i]?.status === "queued" || up[i]?.status === "uploading") && <button type="button" onClick={() => cancelUp(i)} title={T("取消上传")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute top-0.5 right-0.5 rounded-full bg-black/60 text-white p-0.5 hover:bg-red-600" data-testid={tid(`pending-cancel-${i}`)}><X className="h-3 w-3" /></button>
                : <button type="button" onClick={() => rm(i)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute top-0.5 right-0.5 rounded-full bg-black/60 text-white p-0.5 hover:bg-red-600" data-testid={tid(`pending-rm-${i}`)}><X className="h-3 w-3" /></button>}
            </div>
          ))}
        </div>
      )}
      {todo && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/60 px-2 py-1.5 text-xs" data-testid={tid("todo-panel")}>
          <ListTodo className="h-3.5 w-3.5 text-violet-600" /><span className="font-semibold text-violet-800">{T("待办")}</span>
          <select value={effAssignee} onChange={(e) => setAssignee(e.target.value)} className="h-7 text-xs border border-violet-200 rounded px-1.5 bg-white max-w-[200px]" data-testid={tid("todo-assignee")}>
            <option value="">{cands ? T("负责人（可 @ 带入）…") : T("加载中…")}</option>
            {(cands || []).map((u) => <option key={u.username} value={u.username}>{u.name}{u.nameZh ? ` ${u.nameZh}` : ""}{u.isDesign ? "" : ` · ${u.role}`}</option>)}
          </select>
          <label className="inline-flex items-center gap-1 text-slate-600">{T("完成日期")}<input type="date" value={due} min={usToday()} onChange={(e) => setDue(e.target.value)} className="h-7 text-xs border border-violet-200 rounded px-1.5 bg-white" data-testid={tid("todo-due")} /></label>
          {/* 【2026-10-30】待办面板本身是一行紧凑工具条，用 compact 形态。 */}
          <UsDateHint compact testid={tid("todo-due-tz")} />
          <span className="text-[10px] text-slate-500">{T("发送后负责人需接受 / 拒绝 / 修改完成时间；拒绝由设计经理批准或要求继续；每步同步相关同事")}</span>
        </div>
      )}
      {upList.length > 0 && <p className="text-[11px] text-violet-700 flex items-center gap-2" data-testid={tid("progress")} data-done={upDone} data-total={upList.length}>{upLive && <Loader2 className="h-3 w-3 animate-spin" />}{T("上传附件 {0}/{1}", [upDone, upList.length])}{upLive && <button type="button" onClick={cancelAll} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-500 hover:text-red-600 underline" data-testid={tid("progress-cancel-all")}>{T("全部取消")}</button>}</p>}
    </>
  );
  return (
    <div className={`rounded-lg border space-y-2 ${parentId ? "bg-violet-50/40 border-violet-200 p-2" : `bg-white border-slate-200 ${compact ? "p-2" : "p-3"}`}`} data-testid={tid("composer")} data-todo={todo ? 1 : 0} onPaste={onPaste}>
      {/* 负责人芯片（2026-10-19 用户需求）：位于输入框正上方，点选即提醒；
          选项只来自项目有效负责人（US / CN / 交图员 / 协作负责人），不再弹 @ 下拉。 */}
      {requireOwner && (
        <div className="flex flex-wrap items-center gap-1" data-testid={tid("owners")}>
          <span className="text-[11px] font-semibold text-slate-500">{T("提醒负责人（必选）")}</span>
          {(ownerOptions || []).length === 0 ? (
            <span className="text-[11px] text-amber-600" data-testid={tid("owners-empty")}>{T("项目尚未配置负责人，请先在矩阵「负责人」列设置")}</span>
          ) : (ownerOptions || []).map((u) => {
            const on = owners.includes(u.username);
            const src = OWNER_SOURCE_LABEL[u.source];
            return (
              <button key={u.username} type="button"
                onClick={() => setOwners((v) => (on ? v.filter((x) => x !== u.username) : [...v, u.username]))}
                title={`${src ? T(src) : T("负责人")} · ${u.name || u.username}`}
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold ${on ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"}`}
                data-testid={`${tid("owner-pick")}-${u.username}`} data-source={u.source || ""}>
                {src && <span className={`text-[9px] ${on ? "text-violet-100" : "text-slate-400"}`}>{T(src)}</span>}
                {u.name || u.username}
              </button>
            );
          })}
          {owners.length > 0 && <span className="text-[10px] font-semibold text-violet-600" data-testid={tid("owners-count")}>{T("已选 {0}", [owners.length])}</span>}
        </div>
      )}
      {/* 阶段顺序锁（2026-10-19 用户需求）：上一阶段未完成时，下一阶段会话不能发言。
          这里把原因直接贴在输入框上方，并禁用输入 / 附件 / 发送，
          避免用户敲完一大段才被后端 400 拦回来。 */}
      {locked && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800" data-testid={tid("locked")}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{lockReason || T("上一阶段尚未完成，本阶段会话暂不能发送消息。")}</span>
        </div>
      )}
      {/* 待发送附件 / 待办面板 / 上传进度：一律在输入行**上方**，不挤压输入框位置（见 pendingTray 注释） */}
      {pendingTray}
      <div className="flex flex-wrap gap-2 items-center">
        {withStage && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Layers className="h-3.5 w-3.5 text-violet-500" />
          <select value={stage} onChange={(e) => changeStage(e.target.value)} className={`text-xs border rounded-md px-2 py-1.5 bg-white max-w-[200px] ${stage ? "border-violet-300 text-violet-700 font-semibold" : "border-slate-200 text-slate-600"}`} data-testid={tid("stage")} data-auto={autoStage ? 1 : 0} title={autoStage ? T("阶段已按附件文件名自动识别，可手动修改") : T("选择本条评论针对的阶段内容")}>
            <option value="">{T("不指定阶段")}</option>
            {Object.entries(groups).map(([g, ss]) => <optgroup key={g} label={T(g)}>{ss.map((s) => <option key={s.key} value={s.key}>{T(s.name)}</option>)}</optgroup>)}
          </select>{autoStage && <span className="inline-flex items-center gap-0.5 text-[10px] text-violet-600 font-semibold" data-testid={tid("stage-auto")}><Sparkles className="h-3 w-3" />{T("自动识别")}</span>}</span>}
        {/* 阶段会话（fixedStage）一律不出 @ 下拉：提醒对象由负责人芯片决定。
            回复框虽然不再强制选负责人，但同样留在这个口径里，避免两个输入框行为不一致。 */}
        <MentionInput tid={tid("input")} value={t} onChange={setT} onSubmit={send} autoFocus={autoFocus} multiline dropUp={dropUp} candidates={mentionCandidates} disableMentions={requireOwner || !!fixedStage} disabled={locked}
          placeholder={locked ? T("上一阶段完成后才能在本阶段发言") : (placeholder || (requireOwner ? T("写消息…（发送前须选择负责人）") : T("写评论，输入 @ 提醒同事…")))} className={`flex-1 text-sm border rounded-md px-3 py-1.5 min-w-0 ${locked ? "border-amber-200 bg-amber-50/40 text-slate-400 cursor-not-allowed" : "border-slate-200"}`} />
        <input ref={inp} type="file" multiple accept={ACCEPT} className="hidden" onChange={pick} data-testid={tid("file-input")} />
        <button type="button" onClick={() => inp.current?.click()} disabled={busy || locked} title={T("添加附件：图片 / PDF / 视频 / 图纸 · ≤200MB · 也可直接拖拽到评论区")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 h-8 px-2 rounded-md border text-xs font-semibold ${pend.length ? "border-violet-300 text-violet-700 bg-violet-50" : "border-slate-200 text-slate-500 hover:text-slate-800"} disabled:opacity-50`} data-testid={tid("attach")}><Paperclip className="h-3.5 w-3.5" />{pend.length || ""}</button>
        {!parentId && meta?.canEdit !== false && <button type="button" onClick={openTodo} disabled={busy || locked} title={T("把这条评论转为待办：指定负责人与完成日期，对方需接受 / 拒绝 / 改期")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 h-8 px-2 rounded-md border text-xs font-semibold ${todo ? "border-violet-500 text-white bg-violet-600" : "border-slate-200 text-slate-500 hover:text-slate-800"} disabled:opacity-50`} data-testid={tid("todo-toggle")} data-on={todo ? 1 : 0}><ListTodo className="h-3.5 w-3.5" />{T("转为待办")}</button>}
        <Button size="sm" data-testid={tid("send")} onClick={send} disabled={busy || locked || (requireOwner && !owners.length) || (!t.trim() && !pend.length)} className="bg-slate-900 hover:bg-slate-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        {/* 不可发送时在按钮右侧直接写明原因（2026-10-22），不让用户对着灰按钮猜 */}
        {blockReason && <span data-testid={tid("send-blocked")} title={blockReason} className="max-w-[190px] text-[10px] font-semibold leading-tight text-amber-600">{blockReason}</span>}
        {onCancel && <button type="button" onClick={onCancel} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-700" data-testid={tid("cancel")}><X className="h-4 w-4" /></button>}
      </div>
    </div>
  );
};

const Placeholder = ({ kind, name }) => (
  <span className="h-full w-full flex flex-col items-center justify-center gap-0.5 text-slate-500">
    {kind === "pdf" ? <FileText className="h-5 w-5 text-red-500" /> : kind === "video" ? <Film className="h-5 w-5 text-sky-600" /> : <FileIcon className="h-5 w-5" />}
    <span className="text-[9px] font-bold">{extOf(name)}</span>
  </span>
);

const Attachments = ({ c, sfx, onOpen }) => (
  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`dv3-comment-files-${c.id}${sfx}`} data-count={c.files.length}>
    {c.files.map((f, i) => (
      <div key={f.fileId} className="w-40">
        <button type="button" onClick={() => onOpen(i)} title={`${f.name} · ${fmtSize(f.size)} · ${T("点击放大")}`} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 relative h-20 w-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 ring-violet-300 text-left" data-testid={`dv3-comment-file-${c.id}-${i}${sfx}`} data-kind={f.kind}>
          <Placeholder kind={f.kind} name={f.name} />
          {f.kind === "photo" && <img src={fileUrl(f.fileId)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
          {f.kind === "pdf" && <img src={thumbUrl(f.fileId)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover bg-white" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
          {f.kind === "video" && <><video src={`${fileUrl(f.fileId)}#t=0.1`} preload="metadata" muted playsInline className="absolute inset-0 h-full w-full object-cover bg-black" /><span className="absolute inset-0 flex items-center justify-center"><span className="rounded-full bg-black/55 p-1.5"><Play className="h-4 w-4 text-white" /></span></span></>}
          <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] truncate px-1 leading-4">{f.name}</span>
        </button>
        <FileUploadDate value={f.uploadedAt} className="block mt-0.5 leading-3" />
      </div>
    ))}
  </div>
);
